import type {EagColumnState} from '../../../../../types/EagColumnState';

export type ToggleAllState = {
  isAllEnabled: boolean;
  isIndeterminate: boolean;
  visibleColumnKeys: string[];
};

/**
 * Derives the state of the "toggle all" checkbox based on the current
 * column states and the set of keys matching the active search.
 *
 * Only columns whose keys are in `matchingKeys` are considered.
 * This makes the toggle all scope-aware: when search is active it only
 * affects the filtered subset.
 */
export const computeToggleAllState = (
  columnStates: EagColumnState[],
  matchingKeys: Set<string>
): ToggleAllState => {
  const visibleColumnKeys: string[] = [];
  for (const cs of columnStates) {
    if (matchingKeys.has(cs.key)) {
      visibleColumnKeys.push(cs.key);
    }
  }

  if (visibleColumnKeys.length === 0) {
    return {isAllEnabled: false, isIndeterminate: false, visibleColumnKeys};
  }

  let enabledCount = 0;
  for (const key of visibleColumnKeys) {
    const cs = columnStates.find((s) => s.key === key);
    if (cs && !(cs.hidden ?? false)) {
      enabledCount++;
    }
  }

  const isAllEnabled = enabledCount === visibleColumnKeys.length;
  const isIndeterminate = enabledCount > 0 && !isAllEnabled;

  return {isAllEnabled, isIndeterminate, visibleColumnKeys};
};
